import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  TemplateRef,
  OnDestroy,
  Inject
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import {
  IContrato,
  IGetPerfilQueryParams,
  IGetTodosContratosPorGrupoEconomicoQueryParams,
  IListaPaginada,
  IPerfil,
  IUsuario
} from 'src/app/interfaces';
import { IToken } from 'src/app/interfaces/token/IToken';
import { ListaPaginada } from 'src/app/models';
import {
  AuthenticationService,
  ContratoService,
  PerfilService,
  SenhaService,
  UsuarioService
} from 'src/app/services';
import { ModalGeneralComponent } from '../modal-general/modal-general.component';
import { ECompany, ETipo } from 'src/app/enums';
import { Environment } from 'src/app/types';
import { ENVIRONMENT } from 'src/app/constants';

@Component({
  selector: 'app-modal-editar-usuario',
  templateUrl: './modal-editar-usuario.component.html',
  styleUrls: ['./modal-editar-usuario.component.scss']
})
export class ModalEditarUsuarioComponent implements OnDestroy {
  @Output()
  updateUser = new EventEmitter();
  @ViewChild('ModalEditarUsuario')
  modalEditarUsuario: TemplateRef<any>;
  @ViewChild(ModalGeneralComponent)
  modalGeneral: ModalGeneralComponent;
  subscriptions$ = new Subscription();
  formEditaUsuario: FormGroup;
  token: IToken;
  perfis = new ListaPaginada<IPerfil>();
  contratos = new ListaPaginada<IContrato>();
  perfilSelecionado?: IPerfil;
  usuarioEdicao: IUsuario;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private usuarioService: UsuarioService,
    private perfilService: PerfilService,
    private contratoService: ContratoService,
    private authenticationService: AuthenticationService,
    private senhaService: SenhaService,
    @Inject(ENVIRONMENT) public environment: Environment
  ) {}

  exibirOpcaoSelectContrato(env: Environment) {
    return (valor: IContrato) => {
      if (env.company === ECompany.APNETWORKS) {
        return `${valor.codContrato} - ${
          valor.nomeCliente || valor.nomeFantasiaCliente || 'Sem dados'
        }`;
      }
      if (env.company === ECompany.KM_DO_BRASIL) {
        return `${valor.codContrato} - ${
          valor.nomeFantasiaCliente || valor.nomeCliente || 'Sem dados'
        }`;
      }
      return 'Sem dados';
    };
  }

  valorOpcaoSelectContrato({ codContrato }: IContrato): string {
    return codContrato;
  }

  getPerfis(onSuccess: () => void) {
    const getPerfilQueryParams: IGetPerfilQueryParams = {
      idUsuario: this.usuarioEdicao.id,
      paginar: false
    };

    if (this.usuarioEdicao.tipoUsuario === ETipo.INTERNO) {
      getPerfilQueryParams.perfilInterno = true;
    }

    this.subscriptions$.add(
      this.perfilService.getPerfil(getPerfilQueryParams).subscribe({
        next: ({ data }) => {
          this.perfis = data;
          onSuccess();
        }
      })
    );
  }

  getContratosUsuario(
    usuarioId: number,
    onSuccess: (contratos: IListaPaginada<IContrato>) => void
  ) {
    this.subscriptions$.add(
      this.contratoService
        .getContratosUsuario({
          idUsuario: this.token.id,
          usuarioId: usuarioId,
          paginar: false
        })
        .subscribe({
          next: ({ data }) => {
            onSuccess(data);
          }
        })
    );
  }

  getTodosContratosPorGrupoEconomico(codGrupoEconomico?: string) {
    const queryParams: IGetTodosContratosPorGrupoEconomicoQueryParams = {
      idUsuario: this.token.id,
      codGrupoEconomico:
        codGrupoEconomico || this.usuarioEdicao.codGrupoEconomico,
      paginar: false
    };
    if (
      !codGrupoEconomico &&
      this.formEditaUsuario.value?.codContrato.length > 0
    ) {
      queryParams.codContrato = this.formEditaUsuario.value?.codContrato[0];
    }

    this.subscriptions$.add(
      this.contratoService
        .getTodosContratosPorGrupoEconomico(queryParams)
        .subscribe({
          next: ({ data }) => {
            this.contratos = data;
          }
        })
    );
  }

  editarUsuario() {
    const formEditaUsuarioValor = this.formEditaUsuario.getRawValue();
    this.subscriptions$.add(
      this.usuarioService.putUsuario(formEditaUsuarioValor).subscribe({
        next: () => {
          if (formEditaUsuarioValor.alterarSenha) {
            this.senhaService
              .putAlterarSenha({
                idUsuario: formEditaUsuarioValor.idUsuario,
                usuarioId: formEditaUsuarioValor.usuarioId,
                senha: formEditaUsuarioValor.senha
              })
              .subscribe();
          }
        },
        complete: () => {
          this.updateUser.emit();
          this.fecharModal();
        }
      })
    );
  }

  preencherFormulario() {
    this.formEditaUsuario.patchValue({
      idUsuario: this.token.id,
      usuarioId: this.usuarioEdicao.id,
      tipoUsuario: this.usuarioEdicao.tipoUsuario,
      idPerfil: this.usuarioEdicao.perfilID,
      nome: this.usuarioEdicao.nome,
      telefone: this.usuarioEdicao.telefone,
      celular: this.usuarioEdicao.celular
    });
  }

  listarPermissoes() {
    this.modalGeneral.openModal({ size: 'sm' });
  }

  openModal(usuario: IUsuario) {
    this.token = this.authenticationService.obterToken();
    this.formEditaUsuario = this.fb.group(
      {
        idUsuario: [0, [Validators.required]],
        usuarioId: [0, [Validators.required]],
        tipoUsuario: ['', [Validators.required]],
        idPerfil: [0, [Validators.required]],
        nome: ['', [Validators.required]],
        telefone: [''],
        celular: [''],
        codContrato: [[]],
        alterarSenha: [false],
        senha: [
          '',
          [
            Validators.pattern(
              /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&#]).{8,}/
            )
          ]
        ]
      },
      {
        validators: [
          (control: AbstractControl) => {
            const { tipoUsuario, telefone, celular, codContrato } =
              control.value;

            if (tipoUsuario === ETipo.INTERNO) {
              return null;
            }

            if (!telefone && !celular) {
              return { formularioUsuarioExternoInvalido: true };
            }

            if (!usuario.codGrupoEconomico && codContrato.length === 0) {
              return { formularioUsuarioExternoInvalido: true };
            }

            return null;
          }
        ]
      }
    );
    this.subscriptions$.add(
      this.formEditaUsuario.get('alterarSenha')?.valueChanges.subscribe({
        next: (v) => {
          const senhaControl = this.formEditaUsuario.get('senha');
          if (v) {
            senhaControl?.addValidators(Validators.required);
            senhaControl?.updateValueAndValidity();
            return;
          }

          senhaControl?.setValue('');
          senhaControl?.removeValidators(Validators.required);
          senhaControl?.updateValueAndValidity();
        }
      })
    );
    this.usuarioEdicao = usuario;
    this.getPerfis(() => {
      this.subscriptions$.add(
        this.formEditaUsuario.get('idPerfil')?.valueChanges.subscribe({
          next: (v) => {
            this.perfilSelecionado = this.perfis.registros.find(
              (el) => el.id === v
            );
          }
        })
      );
      this.preencherFormulario();
    });
    if (
      this.token.tipoUsuario === ETipo.INTERNO &&
      this.usuarioEdicao.tipoUsuario === ETipo.EXTERNO &&
      !this.usuarioEdicao.codGrupoEconomico
    ) {
      this.getContratosUsuario(this.usuarioEdicao.id, ({ registros }) => {
        this.formEditaUsuario.patchValue({
          codContrato: registros.map((el) => el.codContrato)
        });
        this.getTodosContratosPorGrupoEconomico();
      });
    }

    if (
      this.token.tipoUsuario === ETipo.EXTERNO &&
      this.usuarioEdicao.tipoUsuario === ETipo.EXTERNO &&
      !this.usuarioEdicao.codGrupoEconomico
    ) {
      this.getContratosUsuario(this.usuarioEdicao.id, ({ registros }) => {
        this.formEditaUsuario.patchValue({
          codContrato: registros.map((el) => el.codContrato)
        });

        if (this.token.codGrupoEconomico) {
          this.getTodosContratosPorGrupoEconomico(this.token.codGrupoEconomico);
        }
      });
    }

    if (
      this.token.tipoUsuario === ETipo.EXTERNO &&
      !this.token.codGrupoEconomico
    ) {
      this.getContratosUsuario(this.token.id, (contratos) => {
        this.contratos = contratos;
      });
    }

    this.modalService.open(this.modalEditarUsuario, {
      beforeDismiss: () => {
        this.perfilSelecionado = undefined;
        return true;
      },
      centered: true,
      size: 'lg'
    });
  }

  fecharModal() {
    this.modalService.dismissAll();
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
